import { useEffect, useMemo, useRef, useState } from 'react';
import { api, APIError } from '../../../lib/api';
import { handleApiError } from '../../../lib/errors';
import { sendToTerminal } from '../../../lib/terminalLogger';
import { OrderObject } from '../types/objects';
import { SaveValidationResult, SaveValidationState } from '../types/saveValidation';

interface UseSavePrevalidationParams {
  selectedPedido: any;
  selectedItem: any;
  selectedObjects: OrderObject[];
  enabled: boolean;
  /** Save Temp usa pedido fake — não chama pré-validação contra orçamento real */
  bypassOfficialValidation?: boolean;
  debounceMs?: number;
}

const BYPASS_VALIDATION_RESULT: SaveValidationResult = {
  canSave: true,
  status: 'valid',
  blockingReasons: [],
  warnings: [],
  metrics: {
    totalObjectsWithQuantity: 0,
    totalAreaWithQuantity: 0,
    metrosVendidos: null,
    unidade: 'un',
  },
  validatedAt: new Date(0).toISOString(),
};

interface PreValidateResponse {
  ok?: boolean;
  result?: SaveValidationResult;
}

function buildRevision(orcId: number, oitId: number, selectedObjects: OrderObject[]) {
  const objectSignature = selectedObjects
    .map((obj) => [
      obj.staticId || obj.id,
      obj.width,
      obj.height,
      obj.quantity,
      obj.include === false ? 0 : 1,
    ].join(':'))
    .join('|');

  return `${orcId}:${oitId}:${objectSignature}`;
}

export function useSavePrevalidation({
  selectedPedido,
  selectedItem,
  selectedObjects,
  enabled,
  bypassOfficialValidation = false,
  debounceMs = 400,
}: UseSavePrevalidationParams) {
  const [state, setState] = useState<SaveValidationState>({ phase: 'idle' });
  const requestSeqRef = useRef(0);

  const payload = useMemo(() => {
    const orcId = Number(selectedPedido?.orc_id ?? 0);
    const oitId = Number(selectedItem?.oit_id ?? 0);
    if (!orcId || !oitId) {
      return null;
    }

    const revision = buildRevision(orcId, oitId, selectedObjects);
    return {
      orc_id: orcId,
      oit_id: oitId,
      revision,
      objetos: selectedObjects.map((obj) => ({
        objeto_id: obj.staticId || obj.id,
        guid: obj.guid,
        nome: obj.nome,
        largura: obj.width,
        altura: obj.height,
        quantidade: obj.quantity,
        include: obj.include !== false,
      })),
    };
  }, [selectedPedido?.orc_id, selectedItem?.oit_id, selectedObjects]);

  useEffect(() => {
    if (bypassOfficialValidation) {
      const revision = payload?.revision ?? 'temp-save';
      setState({
        phase: 'valid',
        revision,
        result: { ...BYPASS_VALIDATION_RESULT, revision },
      });
      return;
    }

    if (!enabled || !payload) {
      setState({ phase: 'idle' });
      return;
    }

    const currentSeq = requestSeqRef.current + 1;
    requestSeqRef.current = currentSeq;

    setState({ phase: 'pending', revision: payload.revision });

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await api('/cdr/arquivos/pre-validar-salvamento', {
          method: 'POST',
          body: JSON.stringify(payload),
          signal: controller.signal,
        }) as PreValidateResponse;

        if (requestSeqRef.current !== currentSeq) {
          return;
        }

        const result = response?.result;
        if (!result) {
          setState({
            phase: 'error',
            revision: payload.revision,
            message: 'Não foi possível validar o pedido.',
          });
          return;
        }

        setState(
          result.canSave
            ? { phase: 'valid', revision: payload.revision, result }
            : { phase: 'invalid', revision: payload.revision, result },
        );
      } catch (error) {
        if (controller.signal.aborted || requestSeqRef.current !== currentSeq) {
          return;
        }

        const message = error instanceof APIError
          ? handleApiError(error).message
          : error instanceof Error
            ? error.message
            : 'Não foi possível validar o pedido.';

        sendToTerminal('❌ [SAVE_PREVALIDATION] Falha na pré-validação', {
          message,
          orcId: payload.orc_id,
          oitId: payload.oit_id,
        });

        setState({
          phase: 'error',
          revision: payload.revision,
          message,
        });
      }
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [bypassOfficialValidation, debounceMs, enabled, payload]);

  const primaryMessage = useMemo(() => {
    if (state.phase === 'invalid') {
      return state.result.blockingReasons[0]?.message || 'Existem bloqueios para salvar.';
    }
    if (state.phase === 'error') {
      return state.message;
    }
    if (state.phase === 'pending') {
      return 'Validando pedido...';
    }
    if (state.phase === 'valid' && state.result.status === 'warning') {
      return state.result.warnings[0]?.message || null;
    }
    return null;
  }, [state]);

  return {
    saveValidationState: state,
    saveValidationMessage: primaryMessage,
  };
}
